"use client";

import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { aggregateItemsFromCollection } from "directus/src/operations";

export const useTimesheetJobAggregates = (timesheetData = []) => {
  // Extract unique job IDs from timesheet data
  const uniqueJobIds = useMemo(() => {
    const jobIds = new Set();
    timesheetData.forEach((item) => {
      if (item?.id) {
        jobIds.add(item.id);
      }
    });
    return Array.from(jobIds);
  }, [timesheetData]);

  // Fetch aggregate data for each unique job
  const jobAggregateQueries = useQueries({
    queries: uniqueJobIds.map((jobId) => ({
      queryKey: ["job-timesheet-aggregate", jobId],
      queryFn: async () => {
        const query = {
          aggregate: {
            sum: ["logged_hours", "approved_hours", "unapproved_hours"],
          },
          query: {
            filter: {
              job: { id: { _eq: jobId } },
            },
          },
        };

        const response = await aggregateItemsFromCollection("timesheet", query);

        return {
          jobId,
          logged_hours: response?.[0]?.sum?.logged_hours || 0,
          approved_hours: response?.[0]?.sum?.approved_hours || 0,
          unapproved_hours: response?.[0]?.sum?.unapproved_hours || 0,
        };
      },
      enabled: !!jobId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    })),
  });

  // Create lookup maps for aggregate data and loading states
  const aggregateDataMap = useMemo(() => {
    const map = {};
    jobAggregateQueries.forEach((query) => {
      if (query.data) {
        map[query.data.jobId] = query.data;
      }
    });
    return map;
  }, [jobAggregateQueries]);

  const loadingStateMap = useMemo(() => {
    const map = {};
    jobAggregateQueries.forEach((query, index) => {
      if (uniqueJobIds[index]) {
        map[uniqueJobIds[index]] = query.isLoading;
      }
    });
    return map;
  }, [jobAggregateQueries, uniqueJobIds]);

  const isAnyLoading = jobAggregateQueries.some((query) => query.isLoading);
  const hasErrors = jobAggregateQueries.some((query) => query.error);

  return {
    uniqueJobIds,
    aggregateDataMap,
    loadingStateMap,
    isAnyLoading,
    hasErrors,
    queries: jobAggregateQueries,
  };
};
